(() => {
  // The signed-out layout has no account panel, so this script would bail out and
  // leave a used ?code= in the address bar plus a stale verifier in storage.
  const incoming = new URLSearchParams(location.search);
  if (incoming.has('code') || incoming.has('error')) {
    if (!document.querySelector('[data-kp-acct]')) {
      for (const key of ['code', 'state', 'error', 'error_description']) incoming.delete(key);
      sessionStorage.removeItem('kp-address-auth');
      const query = incoming.toString();
      history.replaceState(null, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
      return;
    }
  }

  const root = document.querySelector('[data-kp-acct]');
  if (!root) return;
  const guest = document.querySelector('[data-kp-acct-guest]');
  guest?.querySelector('[data-kp-account-login]')?.addEventListener('click', () => {
    // A successful hosted sign-in returns to this page. Allow a fresh silent
    // Customer Account API probe instead of preserving the earlier failure.
    sessionStorage.removeItem('kp-acct-probed');
  });
  const panel = root.querySelector('[data-kp-acct-panel="2"]');
  const list = panel.querySelector('[data-kp-address-list]');
  const status = panel.querySelector('[data-kp-address-status]');
  const form = panel.querySelector('[data-kp-address-form]');
  const title = panel.querySelector('[data-kp-address-form-title]');
  const newButton = panel.querySelector('[data-kp-address-new]');
  const saveButton = form.querySelector('[type="submit"]');
  const clientId = root.dataset.kpCustomerClient;
  // Must byte-match the Headless channel's allowed redirect URL.
  const callbackPath = root.dataset.kpAccountCallback || '/pages/my-account';
  const callback = `${location.origin}${callbackPath}`;
  const pendingKey = 'kp-address-auth';
  const tokenKey = 'kp-address-token';
  root.querySelector('[data-kp-account-logout]')?.addEventListener('click', () => {
    for (const key of [pendingKey, tokenKey, 'kp-acct-probed']) sessionStorage.removeItem(key);
  });
  let apiEndpoint;
  let loaded = false;
  let editing = null;
  let navigating = false;
  let saved = { addresses: [], defaultId: null };

  const message = (text, error = false) => {
    status.textContent = text;
    status.style.color = error ? '#a02121' : '';
  };
  const random = () => btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const discover = async (path) => {
    const response = await fetch(path, { credentials: 'omit' });
    if (!response.ok) throw new Error('Shopify account service is unavailable.');
    return response.json();
  };
  const authorize = async (interactive = false, returnTab = 'addresses') => {
    if (!clientId) throw new Error('Address setup is pending.');
    const config = await discover('/.well-known/openid-configuration');
    const verifier = random();
    const state = random();
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    sessionStorage.setItem(pendingKey, JSON.stringify({ verifier, state, callback, returnTab, at: Date.now(), silent: !interactive }));
    const url = new URL(config.authorization_endpoint);
    for (const [key, value] of Object.entries({ client_id: clientId, response_type: 'code', scope: 'openid email customer-account-api:full', redirect_uri: callback, state, code_challenge: challenge, code_challenge_method: 'S256' })) url.searchParams.set(key, value);
    if (!interactive) url.searchParams.set('prompt', 'none');
    navigating = true;
    location.assign(url.toString());
  };
  const finishAuth = async () => {
    const url = new URL(location.href);
    const code = url.searchParams.get('code');
    const error = url.searchParams.get('error');
    if (!code && !error) return;
    const state = url.searchParams.get('state');
    const pending = JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
    sessionStorage.removeItem(pendingKey);
    for (const key of ['code', 'state', 'error', 'error_description']) url.searchParams.delete(key);
    url.searchParams.set('kp_tab', pending?.returnTab === 'profile' ? 'profile' : 'addresses');
    history.replaceState(null, '', url);
    if (!pending || pending.state !== state || Date.now() - pending.at > 300000) throw new Error('Sign-in expired. Open addresses again.');
    if (error === 'login_required') {
      // A silent probe found no session. Leave the shopper signed out instead
      // of throwing an interactive login screen at them.
      if (pending?.silent) { sessionStorage.removeItem(tokenKey); return; }
      await authorize(true, pending?.returnTab); return;
    }
    if (error) throw new Error('Shopify sign-in was cancelled.');
    const config = await discover('/.well-known/openid-configuration');
    const body = new URLSearchParams({ grant_type: 'authorization_code', client_id: clientId, redirect_uri: pending.callback, code, code_verifier: pending.verifier });
    const response = await fetch(config.token_endpoint, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body });
    if (!response.ok) throw new Error('Could not finish Shopify sign-in.');
    const data = await response.json();
    const lifetime = Number(data.expires_in) || 3600;
    sessionStorage.setItem(tokenKey, JSON.stringify({ accessToken: data.access_token, expiresAt: Date.now() + lifetime * 1000 - 30000 }));
    sessionStorage.removeItem('kp-acct-probed');
  };
  const authReady = finishAuth();
  const graph = async (query, variables = {}, returnTab = 'addresses') => {
    await authReady;
    if (navigating) return null;
    if (!clientId) throw new Error('Address setup is pending.');
    const token = JSON.parse(sessionStorage.getItem(tokenKey) || 'null');
    if (!token || token.expiresAt < Date.now()) { await authorize(false, returnTab); return null; }
    if (!apiEndpoint) apiEndpoint = (await discover('/.well-known/customer-account-api')).graphql_api;
    const response = await fetch(apiEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token.accessToken }, body: JSON.stringify({ query, variables }) });
    if (response.status === 401) { sessionStorage.removeItem(tokenKey); await authorize(false, returnTab); return null; }
    if (!response.ok) throw new Error('Shopify could not load addresses.');
    const result = await response.json();
    if (result.errors?.length) throw new Error(result.errors.map((item) => item.message).join(' '));
    return result.data;
  };
  const getAddresses = async () => {
    const data = await graph('query Addresses { customer { defaultAddress { id } addresses(first: 100) { nodes { id firstName lastName company address1 address2 city zoneCode zip countryCode phoneNumber } } } }');
    if (!data) return null;
    return { addresses: data.customer.addresses.nodes, defaultId: data.customer.defaultAddress?.id || null };
  };
  const request = async (payload) => {
    if (!payload) return getAddresses();
    const { action, id, setAsDefault } = payload;
    let query;
    let variables = { id, address: payload.address, defaultAddress: setAsDefault };
    let field;
    if (action === 'create') {
      query = 'mutation Add($address: CustomerAddressInput!, $defaultAddress: Boolean) { customerAddressCreate(address: $address, defaultAddress: $defaultAddress) { customerAddress { id } userErrors { message } } }';
      field = 'customerAddressCreate';
    } else if (action === 'update' || action === 'default') {
      query = 'mutation Edit($id: ID!, $address: CustomerAddressInput, $defaultAddress: Boolean) { customerAddressUpdate(addressId: $id, address: $address, defaultAddress: $defaultAddress) { customerAddress { id } userErrors { message } } }';
      field = 'customerAddressUpdate';
      if (action === 'default') variables = { id, defaultAddress: true };
    } else if (action === 'delete') {
      query = 'mutation Remove($id: ID!) { customerAddressDelete(addressId: $id) { deletedAddressId userErrors { message } } }';
      variables = { id };
      field = 'customerAddressDelete';
    } else throw new Error('Invalid address action.');
    const data = await graph(query, variables);
    if (!data) return null;
    const errors = data[field]?.userErrors || [];
    if (errors.length) throw new Error(errors.map((item) => item.message).join(' '));
    return getAddresses();
  };
  const line = (parent, text) => {
    if (!text) return;
    const p = document.createElement('p');
    p.textContent = text;
    parent.append(p);
  };
  const actionButton = (parent, text, action, address) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = text;
    button.addEventListener('click', () => action(address));
    parent.append(button);
  };
  const render = () => {
    list.replaceChildren();
    if (!saved.addresses.length) {
      message('No address saved yet. Add your first shipping address.');
      return;
    }
    message('');
    saved.addresses.forEach((address) => {
      const item = document.createElement('article');
      item.className = 'kp-acct__address-item';
      line(item, `${address.firstName || ''} ${address.lastName || ''}`.trim() + (address.id === saved.defaultId ? ' · Default' : ''));
      line(item, address.company);
      line(item, [address.address1, address.address2].filter(Boolean).join(', '));
      line(item, [address.city, address.zoneCode, address.zip].filter(Boolean).join(', '));
      line(item, address.countryCode);
      line(item, address.phoneNumber);
      const actions = document.createElement('div');
      actions.className = 'kp-acct__address-actions';
      actionButton(actions, 'Edit', openEdit, address);
      if (address.id !== saved.defaultId) actionButton(actions, 'Set default', () => mutate({ action: 'default', id: address.id }, 'Default address updated.'), address);
      if (address.id !== saved.defaultId) actionButton(actions, 'Delete', remove, address);
      item.append(actions);
      list.append(item);
    });
  };
  const load = async () => {
    try {
      message('Loading addresses…');
      const result = await request();
      if (!result) return;
      saved = result;
      loaded = true;
      render();
    } catch (error) { message(error.message, true); }
  };
  const mutate = async (payload, success) => {
    try {
      saveButton.disabled = true;
      message('Saving…');
      const result = await request(payload);
      if (!result) return;
      saved = result;
      render();
      message(success);
      form.hidden = true;
      editing = null;
    } catch (error) { message(error.message, true); }
    finally { saveButton.disabled = false; }
  };
  const openEdit = (address) => {
    editing = address.id;
    form.reset();
    for (const name of ['firstName', 'lastName', 'company', 'address1', 'address2', 'city', 'zoneCode', 'zip', 'phoneNumber']) {
      form.elements[name].value = address[name] || '';
    }
    form.elements.countryCode.value = address.countryCode || 'IN';
    form.elements.setAsDefault.checked = address.id === saved.defaultId;
    title.textContent = 'Edit shipping address';
    form.hidden = false;
    form.querySelector('input').focus();
  };
  const remove = (address) => {
    if (window.confirm('Delete this saved address?')) mutate({ action: 'delete', id: address.id }, 'Address deleted.');
  };
  newButton.addEventListener('click', () => {
    editing = null;
    form.reset();
    form.elements.countryCode.value = 'IN';
    form.elements.setAsDefault.checked = !saved.addresses.length;
    title.textContent = 'Add shipping address';
    form.hidden = false;
    form.querySelector('input').focus();
  });
  form.querySelector('[data-kp-address-cancel]').addEventListener('click', () => { form.hidden = true; editing = null; });
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    const address = Object.fromEntries(new FormData(form).entries());
    delete address.setAsDefault;
    address.countryCode = String(address.countryCode).trim().toUpperCase();
    mutate({ action: editing ? 'update' : 'create', id: editing, address, setAsDefault: form.elements.setAsDefault.checked }, 'Address saved.');
  });
  root.querySelector('[data-kp-acct-tab="2"]').addEventListener('click', () => { if (!loaded) load(); });
  root.querySelectorAll('[data-kp-address-open]').forEach((button) => button.addEventListener('click', () => { if (!loaded) load(); }));
  const profileForm = root.querySelector('[data-kp-profile-form]');
  const profileStatus = root.querySelector('[data-kp-profile-status]');
  const profileEdit = root.querySelector('[data-kp-profile-edit]');
  const showProfile = async () => {
    try {
      profileStatus.textContent = 'Loading profile…';
      const data = await graph('query Profile { customer { firstName lastName } }', {}, 'profile');
      if (!data) return;
      profileForm.elements.firstName.value = data.customer.firstName || '';
      profileForm.elements.lastName.value = data.customer.lastName || '';
      profileForm.hidden = false;
      profileStatus.textContent = '';
      profileForm.elements.firstName.focus();
    } catch (error) { profileStatus.textContent = error.message; }
  };
  profileEdit.addEventListener('click', showProfile);
  root.querySelector('[data-kp-profile-cancel]').addEventListener('click', () => { profileForm.hidden = true; profileStatus.textContent = ''; });
  profileForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const save = profileForm.querySelector('[type="submit"]');
    save.disabled = true;
    profileStatus.textContent = 'Saving…';
    try {
      const input = { firstName: profileForm.elements.firstName.value.trim(), lastName: profileForm.elements.lastName.value.trim() };
      const data = await graph('mutation UpdateProfile($input: CustomerUpdateInput!) { customerUpdate(input: $input) { customer { firstName lastName } userErrors { message } } }', { input }, 'profile');
      if (!data) return;
      const errors = data.customerUpdate?.userErrors || [];
      if (errors.length) throw new Error(errors.map((item) => item.message).join(' '));
      const first = data.customerUpdate.customer.firstName || '';
      const last = data.customerUpdate.customer.lastName || '';
      root.querySelector('[data-kp-profile-name]').textContent = [first, last].filter(Boolean).join(' ') || 'Not added yet';
      root.querySelector('[data-kp-profile-greeting]').textContent = first || 'there';
      root.querySelector('[data-kp-profile-avatar]').textContent = (first || 'K').charAt(0).toUpperCase();
      profileForm.hidden = true;
      profileStatus.textContent = 'Name updated.';
    } catch (error) { profileStatus.textContent = error.message; }
    finally { save.disabled = false; }
  });
  const swapToSignedIn = (data) => {
    if (guest) guest.hidden = true;
    root.hidden = false;
    const first = (data && data.customer && data.customer.firstName) || '';
    const last = (data && data.customer && data.customer.lastName) || '';
    const greeting = root.querySelector('[data-kp-profile-greeting]');
    if (greeting) greeting.textContent = first || 'there';
    const avatar = root.querySelector('[data-kp-profile-avatar]');
    if (avatar) avatar.textContent = (first || 'K').charAt(0).toUpperCase();
    const name = root.querySelector('[data-kp-profile-name]');
    if (name) name.textContent = [first, last].filter(Boolean).join(' ') || 'Not added yet';
  };

  const hasToken = () => {
    try {
      const token = JSON.parse(sessionStorage.getItem(tokenKey) || 'null');
      return !!(token && token.expiresAt > Date.now());
    } catch (_) { return false; }
  };

  authReady.then(async () => {
    if (navigating) return;
    if (!clientId) return;
    // New customer accounts do not expose a Liquid `customer` object, so the
    // signed-in state has to come from the Customer Account API.
    if (hasToken()) {
      try {
        const data = await graph('query Profile { customer { firstName lastName } }', {}, 'profile');
        if (data) {
          swapToSignedIn(data);
          try {
            const orders = await graph('query Orders { customer { orders(first: 50) { nodes { id } } } }', {}, 'profile');
            const count = orders && orders.customer && orders.customer.orders ? orders.customer.orders.nodes.length : null;
            const el = root.querySelector('[data-kp-profile-orders]');
            if (el && typeof count === 'number') el.textContent = count === 50 ? '50+' : String(count);
          } catch (_) {}
        }
      } catch (_) {}
    } else if (guest && !guest.hidden) {
      // Signed out. Probe once, silently, so a shopper who IS signed in is not
      // stuck on the sign-in panel. A failed probe just leaves the panel alone.
      let probed = false;
      try { probed = sessionStorage.getItem('kp-acct-probed') === '1'; } catch (_) {}
      if (!probed) {
        try { sessionStorage.setItem('kp-acct-probed', '1'); } catch (_) {}
        await authorize(false, 'profile');
      }
      // A silent login_required callback still has kp_tab=profile in its URL.
      // Never open a protected tab for a guest, or it starts another auth probe.
      return;
    }
    const tab = new URLSearchParams(location.search).get('kp_tab');
    if (tab === 'addresses') root.querySelector('[data-kp-acct-tab="2"]').click();
    if (tab === 'profile') showProfile();
  }).catch((error) => { message(error.message, true); profileStatus.textContent = error.message; });
})();
